/**
 * Copyright IBM Corp. 2021-2023
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import React from "react";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import TriFloorPlanSkeleton from "./TriFloorPlanSkeleton.js";
import TriFloorPlanAPI from "./api/TriFloorPlanAPI.js";
import TriFloorPlanUtils from "./util/TriFloorPlanUtils.js";
import TriFloorPlanRegistry from "./plugins/TriFloorPlanRegistry";

const cssBase = "TriFloorPlan";

/**
 * A React component that displays a TRIRIGA floor plan.
 *
 * By default, it displays the floor plan without any interaction capabilities.
 * You can add more capabilities to the floor plan by configuring one or more plugins. The available plugins are:
 *
 * - TriHighlightPlugin: Highlight one or more spaces,
 * - TriLabelsPlugin: Add labels to the spaces,
 * - TriNavPlugin: Display a navigation thumbnail,
 * - TriPanPlugin: Pan the floor plan,
 * - TriPinPlugin: Display a pin on one or more spaces,
 * - TriLastPositionPlugin: Remember the last pan and zoom position,
 * - TriSelectablePlugin: Allow one or more spaces to be selectable by the user,
 * - TriZoomPlugin: Zoom the floor plan
 */
export default class TriFloorPlan extends React.PureComponent {
    static propTypes = {
        className: PropTypes.string,
        /**
         * The ID of the floor plan to be displayed.
         * It can be retrieved from the TriFloorPlanAPI.getFloorplanId method.
         */
        floorPlanId: PropTypes.number.isRequired,
        /**
         * The handler that is called when the floor plan cannot be loaded from the server.
         * The handler receives an error object.
         */
        onFloorPlanLoadError: PropTypes.func,
        /**
         * Handles changes to the svgViewport.  It is called with the new view port in Drawing coordinates.
         * There is some event consolidation. Typically only one event will be received for pan.  Zoom
         * typically produces an event for each step.
         */
        onViewportChange: PropTypes.func,
        /**
         * An array of plugin configuration objects. Each object must have at least two attributes:
         *
         * - type: The type of the plugin being used. It can be one of the following values:
         *  - TriDropablePlugin
         *  - TriHighlightPlugin.type,
         *  - TriLabelsPlugin.type,
         *  - TriNavPlugin.type,
         *  - TriPanPlugin.type,
         *  - TriPinPlugin.type,
         *  - TriLastPositionPlugin.type,
         *  - TriSelectablePlugin.type,
         *  - TriZoomPlugin.type,
         * - id: An ID defined by the application. Each plugin configuration object must have a unique ID.
         *
         * Some plugins may have more attributes. Review the documentation of each plugin to verify all of the attributes that can be passed.
         */
        plugins: PropTypes.array,
        /**
         * When the drawing is not attached, the layer to use for plug-ins that work with space
         * idenity cannot be emperically determined from the drawing.  It can be specified using
         * this property.  It should be a valid label name.  Specifing it disbaled seach for an
         * attached layer. The attribute "tri-element-name" is used to identify the space instead
         * of "tri-record-id"
         */
        spaceLayer: PropTypes.string,
        /**
         * The aria-label value for the floor plan.
         */
        svgAriaLabel: PropTypes.string,
    };

    constructor(props) {
        super(props);
        this.floorPlanDidResize = this.floorPlanDidResize.bind(this);

        this.svgContainerRef = React.createRef();
        this.debouncedUpdateSvgStrokeWidths = debounce(
            this.updateSvgStrokeWidths.bind(this),
            100
        );
        this.pluginInstances = [];
    }

    state = {
        svgViewBox: null,
        floorPlanLoaded: false,
        loading: false,
        floorPlan: null,
    };

    render() {
        const { loading } = this.state;
        const { className } = this.props;

        const classes = cssBase + (className ? " " + className : "");

        return loading ? (
          <TriFloorPlanSkeleton className={`${cssBase}__placeholder`} />
        ) : (
            <div className={classes} ref={this.svgContainerRef} />
        );
    }

    componentDidMount() {
        this.getFloorplan(this.props.floorPlanId);
        this.updatePlugins(this.props.plugins);
        window.addEventListener("resize", this.floorPlanDidResize);
        this.resizeObserver = new ResizeObserver(() => {
            setTimeout(async () => {
                if (
                    !this.state.svgViewBox ||
                    this.state.svgViewBox.scaleX === 0 ||
                    this.state.svgViewBox.scaleY === 0
                ) {
                    const svgViewBox = this.resetSvgViewBox();
                    this.setState({ svgViewBox, floorPlanLoaded: true });
                }
                this.floorPlanDidResize();
            });
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.floorPlanId !== prevProps.floorPlanId) {
            this.getFloorplan(this.props.floorPlanId);
        } else {
            if (this.svgViewBox !== prevState.svgViewBox) {
                this.updateSvgViewBox(this.svgViewBox);
            }
            this.updatePlugins(this.props.plugins);
            if (
                !this.floorPlanLoaded &&
                !this.state.loading &&
                this.floorPlan != null
            ) {
                this.renderFloorplan(this.floorPlan);
            }
        }
    }

    get svgViewBox() {
        return this.state.svgViewBox;
    }

    set svgViewBox(value) {
        if (
            value !== null &&
            !TriFloorPlanUtils.isSvgViewBoxEquals(this.state.svgViewBox, value)
        ) {
            this.setState({ svgViewBox: value });
            this.floorPlanDidPanOrZoom(value);
        }
    }

    get floorPlanLoaded() {
        return this.state.floorPlanLoaded && this.svgElement != null;
    }

    set floorPlanLoaded(value) {
        if (this.state.floorPlanLoaded !== value) {
            this.setState({ floorPlanLoaded: value });
        }
    }

    get floorPlan() {
        return this.state.floorPlan;
    }

    set floorPlan(floorPlan) {
        this.setState({ floorPlan });
    }

    get floorPlanId() {
        return this.props.floorPlanId;
    }

    get svgContainer() {
        return this.svgContainerRef.current;
    }

    get attachedMap() {
        if (this.attachedMapInstance === null) {
            this.attachedMapInstance = TriFloorPlanUtils.createAttachedMap(
                this.svgElement,
                this.props.spaceLayer
            );
        }
        return this.attachedMapInstance;
    }

    get spaceBBoxMap() {
        if (this.spaceBBoxMapInstance === null) {
            const bBoxMap = new Map();
            const spaces = Array.from( this.attachedMap.values() );
            spaces.forEach( (space) => {
                bBoxMap.set( space, space.getBBox() );
            });
            this.spaceBBoxMapInstance = bBoxMap;
        }
        return this.spaceBBoxMapInstance;
    }

    get interactiveMap() {
        if (this.interactiveMapInstance === null) {
            this.interactiveMapInstance =
                TriFloorPlanUtils.createInteractiveMap(
                    this.svgElement,
                    this.props.spaceLayer
                );
        }
        return this.interactiveMapInstance;
    }

    updatePlugins(pluginsProp) {
        const plugins = pluginsProp != null ? pluginsProp : [];
        const updatedInstances = [];
        this.pluginInstances.forEach((instance) => {
            const plugin = plugins.find((item) => item.id === instance.id);
            if (plugin != null) {
                instance.setProps({
                    ...plugin,
                    triFloorPlan: this,
                    floorPlanLoaded: this.floorPlanLoaded,
                    svgViewBox: this.svgViewBox,
                });
                updatedInstances.push(instance);
            } else {
                instance.pluginDidRemove();
            }
        });
        plugins.forEach((plugin) => {
            const instance = this.pluginInstances.find(
                (item) => item.id === plugin.id
            );
            if (instance == null) {
                let instance;
                if (plugin.plugin) {
                    instance = plugin.plugin;
                } else {
                    const PluginClass = TriFloorPlanRegistry.getPlugin(
                        plugin.type
                    );
                    if (PluginClass != null && plugin.id != null) {
                        instance = new PluginClass();
                    }
                }
                if (instance) {
                    instance.setProps({
                        ...plugin,
                        triFloorPlan: this,
                        floorPlanLoaded: this.floorPlanLoaded,
                        vgViewBox: this.svgViewBox,
                    });
                    updatedInstances.push(instance);
                }
            }
        });

        this.pluginInstances = updatedInstances;
    }

    reset() {
        if (this.svgContainer) this.svgContainer.innerHTML = "";
        this.svgElement = null;
        this.svgExtents = null;
        this.svgViewPort = null;
        this.attachedMapInstance = null;
        this.interactiveMapInstance = null;
        this.spaceBBoxMapInstance = null;
        this.currentStrokeWidthScale = null;
        this.initialSvgViewBox = null;
        this.setState({
            svgViewBox: null,
            floorPlanLoaded: false,
            floorPlan: null,
        });
    }

    getFloorplan(floorPlanId) {
        setTimeout(async () => {
            this.reset();
            if (floorPlanId === null) return;
            this.setState({ loading: true });
            try {
                const floorPlan = await TriFloorPlanAPI.getFloorplan(
                    floorPlanId
                );
                this.floorPlan = floorPlan;
            } catch (error) {
                if (this.props.onFloorPlanLoadError) {
                    this.props.onFloorPlanLoadError(error);
                }
            } finally {
                this.setState({ loading: false });
            }
        });
    }

    renderFloorplan(floorplan) {
        this.svgContainer.innerHTML = floorplan;

        setTimeout(() => {
            this.svgElement = this.svgContainer.querySelector("svg");
            this.svgElement.setAttribute("preserveAspectRatio", "xMidYMid");
            if (this.props.svgAriaLabel) {
                this.svgElement.setAttribute(
                    "aria-label",
                    this.props.svgAriaLabel
                );
            }
            const svgViewBox = this.resetSvgViewBox();
            this.sortSpaces( this.svgElement );
            this.setState({ svgViewBox, floorPlanLoaded: true });

            this.resizeObserver.observe(this.svgElement.parentNode);
        }, 1);
    }

    resetSvgViewBox() {
        if (!this.svgElement) {
            return;
        }
        TriFloorPlanUtils.setSvgViewBox(this.svgElement, 0, 0, 0, 0);
        this.svgExtents = TriFloorPlanUtils.getSvgExtents(this.svgElement);
        this.svgViewPort = this.svgElement.getBoundingClientRect();
        const svgViewBox = TriFloorPlanUtils.createInitialSvgViewBox(
            this.svgExtents,
            this.svgViewPort
        );
        this.initialSvgViewBox = svgViewBox;
        return svgViewBox;
    }

    updateSvgViewBox(svgViewBox) {
        if (
            !svgViewBox ||
            !this.svgElement ||
            !svgViewBox.scaleX ||
            !svgViewBox.scaleY
        ) {
            return;
        }

        this.debouncedUpdateSvgStrokeWidths();

        TriFloorPlanUtils.setSvgViewBox(
            this.svgElement,
            svgViewBox.x,
            svgViewBox.y,
            this.svgViewPort.width / svgViewBox.scaleX,
            this.svgViewPort.height / svgViewBox.scaleY
        );
    }

    updateSvgStrokeWidths() {
        this.currentStrokeWidthScale = TriFloorPlanUtils.updateSvgStrokeWidths(
            this.svgElement,
            this.currentStrokeWidthScale
        );
    }

    floorPlanDidResize() {
        const svgViewBox = this.svgViewBox;
        if (
            !svgViewBox ||
            !this.svgElement ||
            !svgViewBox.scaleX ||
            !svgViewBox.scaleY
        ) {
            return;
        }
        this.computeCurrentSvgViewBoxScale();
        this.debouncedUpdateSvgStrokeWidths();

        this.pluginInstances.forEach((instance) =>
            instance.floorPlanDidResize()
        );
    }

    floorPlanDidPanOrZoom(svgViewBox) {
        this.pluginInstances.forEach((instance) =>
            instance.floorPlanDidPanOrZoom(svgViewBox)
        );
        this.handleViewportChange(svgViewBox);
    }

    handleViewportChange = debounce((svgViewBox) => {
        if (typeof this.props.onViewportChange === "function") {
            const viewport = {
                x: svgViewBox.x,
                y: svgViewBox.y,
                width:
                    svgViewBox.scaleX > 0
                        ? this.svgViewPort.width / svgViewBox.scaleX
                        : this.svgExtents.width,
                height:
                    svgViewBox.scaleY > 0
                        ? this.svgViewPort.height / svgViewBox.scaleY
                        : this.svgExtents.height,
            };

            this.props.onViewportChange(viewport);
        }
    }, 200);

    computeCurrentSvgViewBoxScale() {
        if (!this.svgElement) {
            return;
        }
        const oldViewPort = this.svgViewPort;
        this.svgViewPort = this.svgElement.getBoundingClientRect();
        if( this.svgViewPort.height === 0 || this.svgViewPort.width === 0 )
        {
            this.svgViewPort = oldViewPort;
            return;
        }
        this.svgViewBox = TriFloorPlanUtils.computeCurrentSvgViewBoxScale(
            oldViewPort,
            this.svgViewPort,
            this.svgViewBox
        );
        this.initialSvgViewBox = TriFloorPlanUtils.createInitialSvgViewBox(
            this.svgExtents,
            this.svgViewPort
        );
    }

  // This should really happen on the platform before the data is serialized.
  //
  // Sort spaces with the largest on the bottom so sub - spaces are shown on top
  // of their parent
  // Sort order bottom to top:
  //  - Unattached polyline and anything not listed below sorted with largeest
  //    bounding box area on the bottom
  //  - Attached Records
  //    - Grouped by record id
  //    - Sorted within each group larges to smalest bounding box area
  //    - Sorted by larges bbox area larges to smallest
  //  - All lines
  sortSpaces( svgElement )
  {
    const spaces = svgElement.querySelectorAll( "[tri-record-id].tri-attached" );
    if( !spaces || spaces.length < 1 ) return;

    let layer = spaces[ 0 ].parentNode;
    const layerId = layer.id;

    // Multiple copies of the space layer are getting loaded.
    const spaceLayers = [];
    for( let i = 0; i < svgElement.childNodes.length; i++ )
    {
      if( svgElement.childNodes[ i ].id === layerId )
      {
        spaceLayers.push( svgElement.childNodes[ i ] );
      }
    }

    for( let i = 0; i < spaceLayers.length; i++ )
    {
      const notAttached = [];
      const attachedMap = new Map();
      const lines = [];



      layer = spaceLayers[ i ];
      const nextSibling = layer.nextSibling;
      const parent = layer.parentNode;
      for( let i = 0; i < layer.childNodes.length; i++ )
      {
        const node = layer.childNodes[ i ];
        if( node.tagName === "line" )
        {
          lines.push( node );
        }
        else
        {
          let area = 0;
          if( node.getBBox )
          {
            const bBox = node.getBBox();
            area = bBox.height * bBox.width;
          }

          const recordId = node.getAttribute( "tri-record-id" );
          if( recordId )
          {
            let space = attachedMap.get( recordId );
            if( !space )
            {
              space = {
                area:     0,
                recordId: recordId,
                nodes:    [],
              };
              attachedMap.set( recordId, space );
            }
            if( area > space.area ) space.area = area;
            space.nodes.push( { area: area, node: node, } );
          }
          else
          {
            notAttached.push( { area: area, node: node, } );
          }
        }
      }

      notAttached.sort( ( a, b ) => b.area - a.area );
      const attached = Array.from( attachedMap.values() ).sort( ( a, b ) => b.area - a.area );
      attached.forEach( space => space.nodes.sort( ( a, b ) => b.area - a.area ) );

      // Remove the layer before updating to improve rendering speed
      parent.removeChild( layer );
      layer.innerHTML = "";

      notAttached.forEach( el => layer.appendChild( el.node ) );
      attached.forEach( space =>
      {
        space.nodes.forEach( node => layer.appendChild( node.node ) );
      } );
      lines.forEach( line => layer.appendChild( line ) );

      if( nextSibling )
      {
        parent.insertBefore( layer, nextSibling );
      }
      else
      {
        parent.appendChild( layer );
      }
    }
  }
}
